import BasicModal from '@/components/ui/BasicModal';
import { isTelegramMiniApp } from '@/utils/checkRuntime';
import { ModalCommonProps } from '@/types/modal';
import { useTranslation } from 'next-i18next';
import useTheme from '@/theme/useTheme';
import {
  InviteBannerWrapper,
  InviteContent,
  LinkWrapper,
  ShareLinkWrapper,
} from './styles';
import Image from 'next/image';
import Link from 'next/link';
import { getStaticAsset } from '@/utils/staticAsset';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { copyToClipboard } from '@/utils';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { getRushInviterCode } from '@/apis/casino/rush';
import { LoadingButton } from '@mui/lab';
import config from '@/configs';
import { globalQueryKeyReferralCode } from '@/constants/globalQueryKeyMaps';
import { useAtomValue } from 'jotai/index';
import { isLoggedIn } from '@/state/userInfo';

export default function InviteModal(props: ModalCommonProps) {
  const { isOpen, onClose } = props;
  const { t } = useTranslation();
  const isLogin = useAtomValue(isLoggedIn);
  const [shareLink, setShareLink] = useState('');
  const { theme } = useTheme();
  const { data, isLoading, refetch } = useQuery({
    enabled: isOpen && isLogin,
    queryKey: ['rushInviterCode'],
    queryFn: () => getRushInviterCode(),
    onSuccess: (data) => {
      const baseUrl = isTelegramMiniApp
        ? config.env?.endpoints?.telegramBot
        : config.env?.endpoints?.domain;

      setShareLink(
        `${baseUrl}?${globalQueryKeyReferralCode}=${data?.data?.inviteCode}`,
      );
    },
  });

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen]);

  const handleCopy = () => {
    copyToClipboard(shareLink).then(() => {
      toast.success(t('COPY SUCCESS'));
    });
  };

  return (
    <BasicModal open={isOpen} onClose={onClose} title={t('Rush Referral')}>
      <>
        <InviteBannerWrapper>
          <Image
            width={106}
            height={89}
            src={getStaticAsset('/assets/images/casino/lucky-pot/invite.png')}
            alt={'invite image'}
          ></Image>
          <InviteContent>
            <Typography>
              {t(
                'Invite a friend and get awarded for the winnings they won from Moon Rush!',
              )}
            </Typography>
            <Typography
              sx={{
                color: (theme) => theme.palette.primary.main,
                mt: 1,
                mb: 1,
                fontSize: '12px',
              }}
            >
              {t('Invite friends, earn money.')}
            </Typography>
            <Typography
              sx={{
                color: (theme) => theme.palette.primary.main,
                fontSize: '12px',
                textDecoration: 'underline',
              }}
            >
              <Link href={'/profile/referral?tab=rush'}>{t('Learn more')}</Link>
            </Typography>
          </InviteContent>
        </InviteBannerWrapper>
        <ShareLinkWrapper>
          <Typography>{t('Share your Rush Referral link:')}</Typography>
          <LinkWrapper>
            <Typography
              sx={{
                fontSize: '14px',
                marginRight: '8px',
                [theme.breakpoints.down('md')]: {
                  fontSize: '11px',
                },
              }}
            >
              {shareLink}
            </Typography>
            <LoadingButton
              variant="outlined"
              loading={isLoading}
              sx={{
                height: '32px',
                [theme.breakpoints.down('md')]: {
                  fontSize: '12px',
                  padding: '0 4px',
                },
              }}
              onClick={handleCopy}
            >
              {t('COPY')}
            </LoadingButton>
          </LinkWrapper>
        </ShareLinkWrapper>
      </>
    </BasicModal>
  );
}
